import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Sparkles, Check } from "lucide-react";
import { goToAppStore } from "../lib/api";

const LAUNCH_DATE = new Date("2026-10-01T09:00:00Z");

const getRemaining = () => {
  const diff = Math.max(0, LAUNCH_DATE - new Date());
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff / 3600000) % 24),
    mins: Math.floor((diff / 60000) % 60),
    secs: Math.floor((diff / 1000) % 60),
  };
};

function CountdownTimer() {
  const [t, setT] = useState(getRemaining());

  useEffect(() => {
    const id = setInterval(() => setT(getRemaining()), 1000);
    return () => clearInterval(id);
  }, []);

  const units = [
    { label: "Days", value: t.days },
    { label: "Hours", value: t.hours },
    { label: "Minutes", value: t.mins },
    { label: "Seconds", value: t.secs },
  ];

  return (
    <div className="flex items-center gap-3 sm:gap-5" data-testid="coming-countdown">
      {units.map((u, i) => (
        <React.Fragment key={u.label}>
          <div className="flex flex-col items-center">
            <div className="w-[70px] sm:w-24 h-[70px] sm:h-24 rounded-2xl bg-white/[0.05] border border-white/10 backdrop-blur flex items-center justify-center">
              <span className="font-serif text-3xl sm:text-4xl text-stylist-gold tabular-nums">
                {String(u.value).padStart(2, "0")}
              </span>
            </div>
            <span className="mt-3 text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.2em] text-white/40">
              {u.label}
            </span>
          </div>
          {i < units.length - 1 && (
            <span className="font-serif text-2xl sm:text-3xl text-white/20 -mt-6">:</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function ComingSoon({
  badge = "Launching soon",
  title,
  subtitle,
  features = [],
  ctaLabel = "Subscribe today",
  testid = "coming-soon-page",
}) {
  useEffect(() => {
    document.title = `${title}: Coming Soon | Senior Stylist`;
    window.scrollTo(0, 0);
  }, [title]);

  return (
    <div
      className="relative min-h-screen bg-stylist-ink text-white overflow-hidden flex flex-col"
      data-testid={testid}
    >
      <div className="absolute inset-0 ss-grain opacity-[0.07] pointer-events-none" />
      <div
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(197,155,39,0.22), transparent 60%)" }}
      />

      <header className="relative z-10 max-w-6xl w-full mx-auto px-6 py-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-full bg-white flex items-center justify-center">
            <img src="/logo.png" alt="Senior Stylist" className="w-8 h-8 object-contain" />
          </span>
          <span className="font-serif text-xl">Senior Stylist</span>
        </Link>
        <Link
          to="/"
          data-testid="coming-back-home"
          className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>
      </header>

      <main className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-6 py-12">
        <span className="inline-flex items-center gap-2 rounded-full border border-stylist-gold/40 bg-stylist-gold/10 text-stylist-gold px-5 py-2 text-xs font-mono uppercase tracking-[0.28em]">
          <Sparkles className="w-3.5 h-3.5" />
          {badge}
        </span>

        <h1 className="mt-8 font-serif font-light text-4xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-4xl">
          {title}
        </h1>

        <p className="mt-6 text-lg text-white/70 max-w-2xl leading-relaxed">{subtitle}</p>

        {features.length > 0 && (
          <ul
            className="mt-10 grid sm:grid-cols-2 gap-x-8 gap-y-3 text-left max-w-2xl w-full"
            data-testid="coming-features"
          >
            {features.map((f) => (
              <li key={f} className="flex items-start gap-3 text-sm text-white/80">
                <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full bg-stylist-gold/20 flex items-center justify-center">
                  <Check className="w-3 h-3 text-stylist-gold" />
                </span>
                {f}
              </li>
            ))}
          </ul>
        )}

        <div className="mt-12">
          <CountdownTimer />
        </div>

        <button
          onClick={goToAppStore}
          data-testid="coming-cta-button"
          className="mt-12 group inline-flex items-center gap-2 rounded-full bg-stylist-gold text-stylist-ink px-8 py-4 text-sm font-medium hover:bg-white hover:scale-[1.03] transition-all duration-300"
        >
          <Sparkles className="w-4 h-4" />
          {ctaLabel}
        </button>

        <div className="mt-10">
          <span className="eyebrow text-stylist-rose">Senior Stylist · London</span>
        </div>
      </main>
    </div>
  );
}
